import { useState } from "react";
import { toast } from "sonner";
import { User, Building2, Loader2, ArrowRight, Briefcase, Package, Wrench, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api from "@/lib/api";

export default function AccountTypeSetup({ open, onComplete }) {
  const { user, updateUser } = useAuth();
  const { brandName } = useBrand();
  
  const [step, setStep] = useState("select"); // "select" or "company_details"
  const [userType, setUserType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [companyIndustry, setCompanyIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectType = (type) => {
    setUserType(type);
    if (type === "company") {
      setStep("company_details");
    } else {
      // Submit immediately for individual
      handleSubmit(type);
    }
  };

  const handleSubmit = async (type = userType) => {
    if (type === "company") {
      if (!companyName.trim()) {
        toast.error("Please enter your company name");
        return;
      }
      if (!companyIndustry) {
        toast.error("Please select your industry type");
        return;
      }
      if (!companySize) {
        toast.error("Please select your company size");
        return;
      }
    }

    setIsLoading(true);
    try {
      const response = await api.post("/auth/account-type", {
        user_type: type,
        company_name: type === "company" ? companyName.trim() : null,
        company_industry: type === "company" ? companyIndustry : null,
        company_size: type === "company" ? companySize : null
      });
      
      // Update user in context
      updateUser({
        ...user,
        user_type: response.data.user_type,
        account_type_confirmed: true,
        company_name: response.data.company_name,
        company_industry: response.data.company_industry,
        company_size: response.data.company_size
      });
      
      toast.success(type === "company" 
        ? "Welcome! Your business account is ready." 
        : "Welcome! Your personal account is ready."
      );
      
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update account type");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("select");
    setCompanyName("");
    setCompanyIndustry("");
    setCompanySize("");
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} data-testid="account-type-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Welcome to {brandName}!</DialogTitle>
          <DialogDescription>
            {step === "select" 
              ? "How will you be using this account? This helps us personalize your experience."
              : "Tell us about your business"
            }
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Personal Account Card */}
            <button 
              type="button"
              className="text-left w-full"
              onClick={() => handleSelectType("individual")}
              disabled={isLoading}
              data-testid="select-personal"
            >
              <Card 
                className={`cursor-pointer transition-all hover:border-[hsl(var(--ff-primary))] hover:shadow-md h-full ${userType === "individual" && isLoading ? "border-[hsl(var(--ff-primary))] ring-2 ring-[hsl(var(--ff-primary))]/20" : ""}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Personal</h3>
                  <p className="text-sm text-muted-foreground">
                    Track personal finances, set budgets, and manage goals
                  </p>
                  {isLoading && userType === "individual" && (
                    <Loader2 className="h-5 w-5 mx-auto mt-3 animate-spin text-[hsl(var(--ff-primary))]" />
                  )}
                </CardContent>
              </Card>
            </button>

            {/* Business Account Card */}
            <button 
              type="button"
              className="text-left w-full"
              onClick={() => handleSelectType("company")}
              disabled={isLoading}
              data-testid="select-business"
            >
              <Card 
                className={`cursor-pointer transition-all hover:border-[hsl(var(--ff-primary))] hover:shadow-md h-full`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Business</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage invoices, clients, sales, and business analytics
                  </p>
                </CardContent>
              </Card>
            </button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name *</Label>
              <Input
                id="company-name"
                placeholder="Enter your company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                data-testid="company-name-input"
              />
            </div>

            {/* Industry Type */}
            <div className="space-y-2">
              <Label>What type of business are you? *</Label>
              <div className="grid grid-cols-3 gap-3">
                <Card 
                  className={`cursor-pointer transition-all ${companyIndustry === "product" ? "border-[hsl(var(--ff-primary))] bg-[hsl(var(--ff-primary))]/5" : "hover:border-muted-foreground/50"}`}
                  onClick={() => setCompanyIndustry("product")}
                  data-testid="industry-product"
                >
                  <CardContent className="p-4 text-center">
                    <Package className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium">Products</p>
                    <p className="text-xs text-muted-foreground">Sell physical goods</p>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`cursor-pointer transition-all ${companyIndustry === "service" ? "border-[hsl(var(--ff-primary))] bg-[hsl(var(--ff-primary))]/5" : "hover:border-muted-foreground/50"}`}
                  onClick={() => setCompanyIndustry("service")}
                  data-testid="industry-service"
                >
                  <CardContent className="p-4 text-center">
                    <Wrench className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium">Services</p>
                    <p className="text-xs text-muted-foreground">Provide services</p>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`cursor-pointer transition-all ${companyIndustry === "both" ? "border-[hsl(var(--ff-primary))] bg-[hsl(var(--ff-primary))]/5" : "hover:border-muted-foreground/50"}`}
                  onClick={() => setCompanyIndustry("both")}
                  data-testid="industry-both"
                >
                  <CardContent className="p-4 text-center">
                    <Briefcase className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium">Both</p>
                    <p className="text-xs text-muted-foreground">Products & services</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Company Size */}
            <div className="space-y-2">
              <Label>Company Size *</Label>
              <Select value={companySize} onValueChange={setCompanySize}>
                <SelectTrigger data-testid="company-size-select">
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solo">Solo / Freelancer (Just me)</SelectItem>
                  <SelectItem value="small">Small (2-10 employees)</SelectItem>
                  <SelectItem value="medium">Medium (11-50 employees)</SelectItem>
                  <SelectItem value="large">Large (50+ employees)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1" data-testid="back-btn">
                Back
              </Button>
              <Button 
                onClick={() => handleSubmit()} 
                disabled={isLoading || !companyName || !companyIndustry || !companySize}
                className="flex-1"
                data-testid="continue-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === "select" && (
          <p className="text-xs text-center text-muted-foreground">
            You can change this later in Settings
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
